/**
 * Dictionnaire bilingue de la page hub chauffeur (`/chauffeur` · `/en/chauffeur`).
 *
 * Centralise TOUT le contenu + le "chrome" de la home chauffeur (jadis codé en
 * dur dans page.tsx + les 6 composants). La vue partagée ChauffeurHubView et
 * les sections lisent `CHAUFFEUR_HUB[locale]` → un seul markup pour FR et EN,
 * zéro composant dupliqué, zéro texte FR côté EN.
 *
 * Les visuels (StaticImageData) restent dans la vue/les composants (identiques
 * FR/EN) ; ici uniquement les chaînes + la structure de contenu.
 */
import { type I18n } from "@/config/localeText";
import { HUB_FR } from "./hubDict.fr";
import { HUB_EN } from "./hubDict.en";
import { HUB_AR } from "./hubDict.ar";

export type HubLink = { label: string; href: string };
export type HubService = { title: string; text: string; icon?: string };
export type HubLocation = { name: string; description?: string };
export type HubFaq = { q: string; a: string };

export type ChauffeurHubData = {
  hero: {
    eyebrow: string;
    title: string;
    subtitle: string;
    ctaPrimary: HubLink;
    ctaSecondary: HubLink;
    trustLine: string;
    chips: string[];
    bookingTitle: string;
    steps: string[];
    bookingCta: string;
    bookingNote: string;
  };
  services: {
    eyebrow: string;
    heading: string;
    intro: string;
    mostRequested: string;
    priorityBadge: string;
    points: string[];
    items: HubService[];
  };
  indicators: {
    heading: string;
    items: { label: string; value: string; text: string }[];
  };
  motion: {
    eyebrow: string;
    heading: string;
    text: string;
    badge: string;
    signatureText: string;
    leftAlt: string;
    rightAlt: string;
  };
  locations: {
    eyebrow: string;
    heading: string;
    introPrefix: string;
    srPrefix: string;
    corridorsTitle: string;
    corridorNote: string;
    corridorPrefix: string;
    items: HubLocation[];
  };
  process: {
    heading: string;
    intro: string;
    stepWord: string;
    steps: string[];
    leftAlt: string;
    rightAlt: string;
  };
  seoContent: {
    eyebrow: string;
    heading: string;
    paragraphs: string[];
    bullets: string[];
    metrics: { k: string; v: string }[];
    whyTitle: string;
    whyItems: string[];
  };
  atouts: {
    eyebrow: string;
    heading: string;
    intro: string;
    items: { title: string; text: string }[];
  };
  faq: {
    eyebrow: string;
    heading: string;
    items: HubFaq[];
    asideEyebrow: string;
    asideTitle: string;
    asideText: string;
    asideBullets: string[];
  };
  fleet: {
    eyebrow: string;
    heading: string;
    intro: string;
    links: HubLink[];
  };
  complementary: {
    heading: string;
    intro: string;
    links: HubLink[];
  };
  seo: { title: string; description: string; canonical: string };
};

export const CHAUFFEUR_HUB: I18n<ChauffeurHubData> = {
  fr: HUB_FR,
  en: HUB_EN,
  ar: HUB_AR,
};
